/**
 * Profile activity section — Reddit-style tabs of the user's community
 * posts and comments. Visible in both marketplace and community modes.
 *
 * Data comes from ProfilePage.duck (`communityPosts`, `activityComments`)
 * which loadData populates via the /api/community/user-activity endpoint.
 *
 * Tab state lives in the URL query (`?tab=posts` / `?tab=comments`) so
 * a reload / shared link lands on the right tab.
 */

import React from 'react';
import { array, bool, object } from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import { Link, useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment';

import { createResourceLocatorString } from '../../util/routes';
import { parse } from '../../util/urlHelpers';
import routeConfiguration from '../../routeConfiguration';

import css from './ProfilePage.module.css';

const TAB_POSTS = 'posts';
const TAB_COMMENTS = 'comments';
const VALID_TABS = [TAB_POSTS, TAB_COMMENTS];

const stripHtml = (s = '') =>
  s
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const truncate = (s, n) => (s.length > n ? `${s.slice(0, n).trim()}…` : s);

const postPath = post => {
  if (!post?.id) return null;
  const routes = routeConfiguration();
  return createResourceLocatorString(
    'PostPage',
    routes,
    { id: post.id, slug: post.slug || 'no-slug' },
    {}
  );
};

const PostCard = ({ post }) => {
  const path = postPath(post);
  const date = post.createdAt ? moment(post.createdAt).fromNow() : null;
  return (
    <Link to={path} className={css.activityCard}>
      <div className={css.activityCardHeader}>
        <h3 className={css.activityCardTitle}>{post.title}</h3>
        {date ? <span className={css.activityCardMeta}>{date}</span> : null}
      </div>
      {post.excerpt ? <p className={css.activityCardExcerpt}>{post.excerpt}</p> : null}
    </Link>
  );
};

const CommentCard = ({ comment }) => {
  const date = comment.createdAt ? moment(comment.createdAt).fromNow() : null;
  const path = postPath({ id: comment.postId, slug: comment.postSlug });
  return (
    <Link to={path} className={css.activityCard}>
      <div className={css.activityCardMetaRow}>
        <FormattedMessage
          id="SectionUserActivity.commentOnPost"
          values={{
            postTitle: (
              <span className={css.activityCardMetaPostTitle}>{comment.postTitle}</span>
            ),
          }}
        />
        {date ? <span className={css.activityCardMeta}>· {date}</span> : null}
      </div>
      <p className={css.activityCardExcerpt}>
        {truncate(stripHtml(comment.content || ''), 240)}
      </p>
    </Link>
  );
};

const SectionUserActivity = ({
  posts,
  comments,
  inProgress,
  error,
}) => {
  // useHistory / useLocation instead of relying on router props,
  // which ProfilePage doesn't forward (no withRouter in its compose).
  const history = useHistory();
  const location = useLocation();

  // Tab state is URL-backed via ?tab=. Defaults to posts when missing
  // or unknown so a stale param can't break the section.
  const search = parse(location.search);
  const requestedTab = search.tab;
  const activeTab = VALID_TABS.includes(requestedTab) ? requestedTab : TAB_POSTS;

  const setTab = nextTab => {
    const nextSearch = { ...search, tab: nextTab };
    history.push(
      `${location.pathname}?${Object.entries(nextSearch)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&')}`
    );
  };

  const renderTabButton = (tabKey, labelId, count) => (
    <button
      type="button"
      className={classNames(css.activityTab, {
        [css.activityTabActive]: activeTab === tabKey,
      })}
      onClick={() => setTab(tabKey)}
      aria-pressed={activeTab === tabKey}
    >
      <FormattedMessage id={labelId} />
      <span className={css.activityTabCount}>{count}</span>
    </button>
  );

  // Loaded with neither posts nor comments → hide the whole section (matches
  // Reviews/Subscriptions, which also disappear when the user has no data).
  if (!inProgress && !error && posts.length === 0 && comments.length === 0) {
    return null;
  }

  return (
    <div className={classNames(css.sectionContent, css.sectionUserActivity)}>
      <h2 className={css.sectionTitle}>
        <FormattedMessage id="SectionUserActivity.title" />
      </h2>

      <div className={css.activityTabs} role="tablist">
        {renderTabButton(TAB_POSTS, 'SectionUserActivity.postsTab', posts.length)}
        {renderTabButton(TAB_COMMENTS, 'SectionUserActivity.commentsTab', comments.length)}
      </div>

      {inProgress ? (
        <p className={css.activityLoading}>
          <FormattedMessage id="SectionUserActivity.loading" />
        </p>
      ) : error ? (
        <p className={css.activityError}>
          <FormattedMessage id="SectionUserActivity.loadError" />
        </p>
      ) : activeTab === TAB_POSTS ? (
        posts.length === 0 ? (
          <p className={css.activityEmptyText}>
            <FormattedMessage id="SectionUserActivity.emptyPosts" />
          </p>
        ) : (
          <ul className={css.activityList}>
            {posts.map(post => (
              <li key={post.id} className={css.activityItem}>
                <PostCard post={post} />
              </li>
            ))}
          </ul>
        )
      ) : comments.length === 0 ? (
        <p className={css.activityEmptyText}>
          <FormattedMessage id="SectionUserActivity.emptyComments" />
        </p>
      ) : (
        <ul className={css.activityList}>
          {comments.map(comment => (
            <li key={comment.id} className={css.activityItem}>
              <CommentCard comment={comment} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

SectionUserActivity.defaultProps = {
  posts: [],
  comments: [],
  inProgress: false,
  error: null,
};

SectionUserActivity.propTypes = {
  posts: array,
  comments: array,
  inProgress: bool,
  error: object,
};

export default SectionUserActivity;
